import { supabase } from '@/integrations/supabase/client';
import type {
  CoachingAccess,
  CoachingAction,
  CoachingActionOwner,
  CoachingGroupStaff,
  CoachingSubjectSummary,
  NewObservationInput,
  WorkspaceCoachingNote,
} from '@/types/coachingWorkspace';

async function rpc<T>(name: string, args?: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.rpc(name as never, args as never);
  if (error) throw error;
  return data as T;
}

const asNumber = (value: number | string): number => Number(value);

async function rpcPages(name: string, args: Record<string, unknown> = {}): Promise<Record<string, unknown>[]> {
  const rows: Record<string, unknown>[] = [];
  for (let offset = 0; ; offset += 500) {
    const page = await rpc<Record<string, unknown>[]>(name, { ...args, p_limit: 500, p_offset: offset });
    rows.push(...page);
    if (page.length < 500) return rows;
  }
}

export const coachingWorkspaceService = {
  getAccess: () => rpc<CoachingAccess>('coaching_get_access'),

  async listSubjects(): Promise<CoachingSubjectSummary[]> {
    const rows = await rpcPages('coaching_list_subjects');
    return rows.map((row) => ({
      subjectType: row.subject_type as CoachingSubjectSummary['subjectType'],
      subjectId: row.subject_id as string,
      locationCode: row.location_code as string,
      subjectName: row.subject_name as string,
      assignedCoachId: row.assigned_coach_id as string | null,
      lastReviewedOn: row.last_reviewed_on as string | null,
      nextReviewOn: row.next_review_on as string | null,
      reviewVersion: asNumber(row.review_version as number | string),
      visibleNoteCount: asNumber(row.visible_note_count as number | string),
      visibleOpenActionCount: asNumber(row.visible_open_action_count as number | string),
    }));
  },

  async listGroupStaff(on: string): Promise<CoachingGroupStaff[]> {
    const rows = await rpcPages('coaching_list_group_staff', { p_on: on });
    return rows.map((row) => ({
      groupId: row.group_id as string,
      staffId: row.staff_id as string,
      staffName: row.staff_name as string,
      locationCode: row.location_code as string,
    }));
  },

  async listNotes(): Promise<WorkspaceCoachingNote[]> {
    const rows = await rpcPages('coaching_list_notes');
    return rows.map((row) => ({
      id: row.id as string,
      coachId: row.coach_id as string,
      coachName: row.coach_name as string,
      locationCode: row.location_code as string,
      subjectType: row.subject_type as WorkspaceCoachingNote['subjectType'],
      staffSubjectId: row.staff_subject_id as string | null,
      groupId: row.group_id as string | null,
      groupName: row.group_name as string | null,
      subjectName: row.subject_name as string,
      kind: row.kind as WorkspaceCoachingNote['kind'],
      body: row.body as string,
      notedOn: row.noted_on as string,
      status: row.status as WorkspaceCoachingNote['status'],
      hours: asNumber(row.hours as number | string),
      ikkBucket: row.ikk_bucket as WorkspaceCoachingNote['ikkBucket'],
      accessMode: row.access_mode as WorkspaceCoachingNote['accessMode'],
      version: asNumber(row.version as number | string),
      createdAt: row.created_at as string,
      updatedAt: row.updated_at as string,
    }));
  },

  async listActions(): Promise<CoachingAction[]> {
    const rows = await rpcPages('coaching_list_actions');
    return rows.map((row) => ({
      id: row.id as string,
      coachingNoteId: row.coaching_note_id as string,
      body: row.body as string,
      ownerStaffId: row.owner_staff_id as string,
      ownerName: row.owner_name as string,
      dueOn: row.due_on as string,
      completedAt: row.completed_at as string | null,
      completedBy: row.completed_by as string | null,
      version: asNumber(row.version as number | string),
      noteAccessMode: row.note_access_mode as CoachingAction['noteAccessMode'],
      noteLocationCode: row.note_location_code as string,
      noteSubjectType: row.note_subject_type as CoachingAction['noteSubjectType'],
      noteGroupId: row.note_group_id as string | null,
      noteStaffSubjectId: row.note_staff_subject_id as string | null,
      noteSubjectName: row.note_subject_name as string,
    }));
  },

  async listActionOwners(): Promise<CoachingActionOwner[]> {
    const rows = await rpcPages('coaching_list_action_owners');
    return rows.map((row) => ({
      staffId: row.staff_id as string,
      staffName: row.staff_name as string,
      isCoach: row.is_coach as boolean,
      accessMode: row.access_mode as CoachingActionOwner['accessMode'],
      locationCode: row.location_code as string | null,
    }));
  },

  createObservation(input: NewObservationInput): Promise<string> {
    return rpc('coaching_create_observation', {
      p_location_code: input.locationCode,
      p_subject_type: input.subjectType,
      p_staff_subject_id: input.staffSubjectId,
      p_group_id: input.groupId,
      p_kind: input.kind,
      p_body: input.body,
      p_noted_on: input.notedOn,
      p_hours: input.hours,
      p_ikk_bucket: input.ikkBucket,
      p_access_mode: input.accessMode,
      p_action_body: input.actionBody,
      p_action_owner_staff_id: input.actionOwnerStaffId,
      p_action_due_on: input.actionDueOn,
      p_idempotency_key: input.idempotencyKey,
    });
  },

  updateObservation(input: Pick<WorkspaceCoachingNote, 'id' | 'version' | 'body' | 'kind' | 'notedOn' | 'hours' | 'ikkBucket' | 'accessMode'>): Promise<number> {
    return rpc('coaching_update_observation', {
      p_note_id: input.id, p_expected_version: input.version, p_body: input.body,
      p_kind: input.kind, p_noted_on: input.notedOn, p_hours: input.hours,
      p_ikk_bucket: input.ikkBucket, p_access_mode: input.accessMode,
    });
  },

  archiveObservation(noteId: string, expectedVersion: number): Promise<number> {
    return rpc('coaching_archive_observation', { p_note_id: noteId, p_expected_version: expectedVersion });
  },

  setReviewPlan(input: { subject: CoachingSubjectSummary; nextReviewOn: string | null; assignedCoachId: string | null }): Promise<number> {
    return rpc('coaching_set_review_plan', {
      p_location_code: input.subject.locationCode, p_subject_type: input.subject.subjectType,
      p_subject_id: input.subject.subjectId, p_next_review_on: input.nextReviewOn,
      p_assigned_coach_id: input.assignedCoachId, p_expected_version: input.subject.reviewVersion,
    });
  },

  completeReview(input: { subject: CoachingSubjectSummary; reviewedOn: string; nextReviewOn: string | null }): Promise<number> {
    return rpc('coaching_complete_review', {
      p_location_code: input.subject.locationCode, p_subject_type: input.subject.subjectType,
      p_subject_id: input.subject.subjectId, p_reviewed_on: input.reviewedOn,
      p_next_review_on: input.nextReviewOn, p_expected_version: input.subject.reviewVersion,
    });
  },

  createAction(input: { noteId: string; body: string; ownerStaffId: string; dueOn: string; idempotencyKey: string }): Promise<string> {
    return rpc('coaching_create_action', {
      p_note_id: input.noteId, p_body: input.body, p_owner_staff_id: input.ownerStaffId,
      p_due_on: input.dueOn, p_idempotency_key: input.idempotencyKey,
    });
  },

  updateAction(input: Pick<CoachingAction, 'id' | 'version' | 'body' | 'ownerStaffId' | 'dueOn'>): Promise<number> {
    return rpc('coaching_update_action', {
      p_action_id: input.id, p_expected_version: input.version, p_body: input.body,
      p_owner_staff_id: input.ownerStaffId, p_due_on: input.dueOn,
    });
  },

  completeAction(actionId: string, expectedVersion: number): Promise<number> {
    return rpc('coaching_complete_action', { p_action_id: actionId, p_expected_version: expectedVersion });
  },
};
